import { Grip, type LucideIcon } from 'lucide-react'
import React, { type HTMLAttributes } from 'react'
import type { IconType } from 'react-icons/lib'

import { cn } from '@/modules/shared/lib/utils'

import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '../../../modules/shared/components/ui/tooltip'
import { Button } from './ui/button'

type ItemsProps = {
	id: string
	icon: LucideIcon | IconType
	type: string
	disabled: boolean
	label: string
	action?: () => void
}

type FabComponentProps = HTMLAttributes<HTMLDivElement> & {
	items: ItemsProps[]
	bottomMenuOffset?: boolean
	action?: (type: string) => void
}

export function FabComponent({
	items,
	action,
	bottomMenuOffset = false,
	className,
	...props
}: FabComponentProps) {
	const [isOpen, setIsOpen] = React.useState(false)

	return (
		<>
			{items.length === 1 ? (
				<TooltipProvider disableHoverableContent delayDuration={10}>
					<Tooltip>
						<TooltipTrigger
							onClick={items[0]?.action || (() => action?.(items[0].type))}
							className={cn(
								'fixed right-4 z-50 bg-emerald-500 hover:bg-emerald-600 rounded-full h-14 w-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110',
								bottomMenuOffset ? 'bottom-20 lg:bottom-4' : 'bottom-4'
							)}
						>
							{(() => {
								const Icon = items[0].icon;
								return <Icon size={20} className="text-white" />;
							})()}
						</TooltipTrigger>
						<TooltipContent side="left">
							<p>{items[0].label}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				<div
					className={cn(
						'fixed right-4 z-50 flex flex-col items-center',
						bottomMenuOffset ? 'bottom-20 lg:bottom-4' : 'bottom-4',
						className
					)}
					{...props}
				>
					<div className={cn(
						'flex flex-col-reverse items-center gap-1 transition-all duration-500',
						isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
					)}>
						{items.map((item, index) => (
							!item.disabled && (
								<TooltipProvider key={item.id} delayDuration={10}>
									<Tooltip>
										<TooltipTrigger
											onClick={item?.action || (() => action?.(item.type))}
											className={cn(
												'bg-slate-800/90 backdrop-blur-sm hover:bg-slate-700/95 rounded-full h-12 w-12 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110',
												'animate-in slide-in-from-bottom-2'
											)}
											style={{ animationDelay: `${index * 50}ms` }}
										>
											<item.icon size={18} className="text-white" />
										</TooltipTrigger>
										<TooltipContent side="left">
											<p>{item.label}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)
						))}
					</div>

					<button
						type="button"
						onClick={() => setIsOpen(!isOpen)}
						onKeyUp={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
						className={cn(
							'bg-emerald-500 hover:bg-emerald-600 rounded-full h-14 w-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110',
							isOpen && 'rotate-45 mt-2'
						)}
					>
						<Grip className={cn('text-white transition-transform duration-300', isOpen && 'rotate-45')} />
					</button>
				</div>
			)}
		</>
	)
}